import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import App from './App.jsx'
import './index.css'

const isLocalHost = typeof window !== 'undefined'
  && ['localhost', '127.0.0.1'].includes(window.location.hostname)

const PwaUpdatePrompt = ({ updating, onUpdate, onLater }) => (
  <aside
    className="fixed bottom-5 right-5 z-[200] w-[min(400px,calc(100vw-2rem))] rounded-2xl border border-slate-200 bg-white p-5 text-slate-900 shadow-2xl"
    role="dialog"
    aria-live="polite"
    aria-labelledby="pwa-update-title"
    aria-describedby="pwa-update-description"
  >
    <div className="flex items-start gap-3">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-100 text-xl text-emerald-700" aria-hidden="true">↻</span>
      <div>
        <h2 id="pwa-update-title" className="text-base font-black">New update available</h2>
        <p id="pwa-update-description" className="mt-1 text-sm leading-5 text-slate-600">
          A newer version of iTECHS is ready. Update when it is safe to reload this page.
        </p>
      </div>
    </div>
    <div className="mt-4 flex justify-end gap-2">
      <button
        type="button"
        className="rounded-lg px-4 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-100 disabled:opacity-50"
        onClick={onLater}
        disabled={updating}
      >
        Later
      </button>
      <button
        type="button"
        className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-wait disabled:opacity-70"
        onClick={onUpdate}
        disabled={updating}
      >
        {updating ? 'Updating…' : 'Update Now'}
      </button>
    </div>
  </aside>
)

const Root = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [updating, setUpdating] = useState(false)
  const updateSWRef = useRef(null)

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined

    if (isLocalHost) {
      const clearLocalPwaState = async () => {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));

        if ('caches' in window) {
          const cacheKeys = await window.caches.keys();
          await Promise.all(cacheKeys.map((cacheKey) => window.caches.delete(cacheKey)));
        }
      } catch (error) {
        console.warn('Failed to clear local service workers or caches:', error);
      }
      }
      window.addEventListener('load', clearLocalPwaState)
      return () => window.removeEventListener('load', clearLocalPwaState)
    }

    let updateIntervalId
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        setUpdateAvailable(true)
      },
      onRegisteredSW(_scriptUrl, registration) {
        registration?.update().catch(() => {});
        updateIntervalId = window.setInterval(
          () => registration?.update().catch(() => {}),
          60_000,
        );
      },
      onRegisterError(error) {
        console.warn('Failed to register the application update service:', error);
      },
    })
    updateSWRef.current = updateSW
    return () => {
      if (updateIntervalId) window.clearInterval(updateIntervalId)
    }
  }, [])

  const applyUpdate = async () => {
    if (!updateSWRef.current || updating) return
    setUpdating(true)
    try {
      await updateSWRef.current(true)
    } catch (error) {
      setUpdating(false)
      console.warn('Failed to activate the latest application version:', error)
    }
  }

  return (
    <>
      <App />
      {updateAvailable && (
        <PwaUpdatePrompt
          updating={updating}
          onUpdate={applyUpdate}
          onLater={() => setUpdateAvailable(false)}
        />
      )}
    </>
  )
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
)
